<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title"><i class="pi pi-trash"></i> سلة المحذوفات</h1>
          <p class="ds-page-header__subtitle">استرجاع أو حذف العناصر المحذوفة نهائيًا من كل أقسام النظام</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="recycle-tabs">
        <button
          v-for="m in store.modules"
          :key="m.key"
          type="button"
          class="recycle-tab"
          :class="{ 'recycle-tab--active': store.activeModule === m.key, 'recycle-tab--disabled': !m.implemented }"
          :disabled="!m.implemented"
          @click="selectModule(m.key)"
        >
          <i class="pi" :class="m.icon || 'pi-circle'"></i>
          <span>{{ m.label }}</span>
          <span v-if="m.implemented && m.count" class="recycle-tab__count">{{ m.count }}</span>
          <span v-else-if="!m.implemented" class="recycle-tab__soon">قريباً</span>
        </button>
      </div>

      <!-- Not-yet-implemented module -->
      <div v-if="!activeModuleInfo?.implemented" class="empty-state empty-state--card">
        <div class="empty-state__icon"><i class="pi pi-hourglass"></i></div>
        <h3 class="empty-state__title">{{ activeModuleInfo?.label }} قريبًا في سلة المحذوفات</h3>
        <p class="empty-state__desc">لم يتم ربط هذه الوحدة بعد. سيتم تفعيلها هنا دون الحاجة لأي تعديل في الواجهة.</p>
      </div>

      <template v-else>
        <!-- Filters -->
        <div class="filter">
          <div class="filter__header">
            <div class="filter__header-icon"><i class="pi pi-filter"></i></div>
            <p class="filter__title">عوامل التصفية</p>
          </div>
          <div class="ds-field-grid ds-field-grid--4">
            <div class="form-field">
              <label class="field-label">بحث</label>
              <input type="text" class="form-control" v-model="store.filters.search" @keyup.enter="reload" placeholder="ابحث..." />
            </div>
            <div class="form-field">
              <label class="field-label">من تاريخ</label>
              <input type="date" class="form-control" v-model="store.filters.date_from" @change="reload" />
            </div>
            <div class="form-field">
              <label class="field-label">إلى تاريخ</label>
              <input type="date" class="form-control" v-model="store.filters.date_to" @change="reload" />
            </div>
            <div class="form-field">
              <label class="field-label">حُذف بواسطة</label>
              <select class="form-select" v-model="store.filters.deleted_by" @change="reload">
                <option value="">الكل</option>
                <option v-for="d in store.deleters" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>
          </div>
          <div class="ds-field-grid ds-field-grid--4 mt-2">
            <div class="form-field">
              <label class="field-label">الترتيب حسب</label>
              <select class="form-select" v-model="store.filters.sort" @change="reload">
                <option value="deleted_at">تاريخ الحذف</option>
                <option value="created_at">تاريخ الإنشاء</option>
              </select>
            </div>
            <div class="form-field">
              <label class="field-label">الاتجاه</label>
              <select class="form-select" v-model="store.filters.direction" @change="reload">
                <option value="desc">الأحدث أولاً</option>
                <option value="asc">الأقدم أولاً</option>
              </select>
            </div>
            <div class="form-field d-flex align-items-end">
              <button class="ds-btn ds-btn--secondary" @click="resetFilters">
                <i class="pi pi-refresh"></i> إعادة تعيين
              </button>
            </div>
          </div>
        </div>

        <!-- Bulk actions -->
        <div class="bulk-bar">
          <label class="bulk-bar__select-all">
            <input type="checkbox" :checked="allSelected" @change="store.toggleSelectAll()" />
            <span>تحديد الكل ({{ store.selected.length }})</span>
          </label>
          <div class="bulk-bar__actions">
            <button class="ds-btn ds-btn--secondary ds-btn--sm" :disabled="!store.selected.length || store.acting" @click="restoreSelected">
              <i class="pi pi-replay"></i> استرجاع المحدد
            </button>
            <button class="ds-btn ds-btn--danger ds-btn--sm" :disabled="!store.selected.length || store.acting" @click="forceDeleteSelected">
              <i class="pi pi-trash"></i> حذف نهائي للمحدد
            </button>
            <button class="ds-btn ds-btn--secondary ds-btn--sm" :disabled="!store.items.length || store.acting" @click="restoreAll">
              <i class="pi pi-replay"></i> استرجاع الكل
            </button>
            <button class="ds-btn ds-btn--danger ds-btn--sm" :disabled="!store.items.length || store.acting" @click="emptyBin">
              <i class="pi pi-exclamation-triangle"></i> إفراغ السلة
            </button>
          </div>
        </div>

        <Loading v-if="store.loading" />

        <template v-else>
          <div v-if="!store.items.length" class="empty-state empty-state--card">
            <div class="empty-state__icon"><i class="pi pi-inbox"></i></div>
            <h3 class="empty-state__title">سلة المحذوفات فارغة</h3>
            <p class="empty-state__desc">لا توجد عناصر محذوفة مطابقة لعوامل التصفية الحالية.</p>
          </div>

          <template v-else>
            <!-- Desktop / tablet table — columns are entirely driven by
                 activeModuleInfo.columns (config/recycle.php), so every
                 module renders here with no per-module template code. -->
            <div class="ds-table-wrap table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3" style="width:2.5rem">
                      <input type="checkbox" :checked="allSelected" @change="store.toggleSelectAll()" />
                    </th>
                    <th v-if="!gridColumns.length">العنصر</th>
                    <th v-for="col in gridColumns" :key="col.path">{{ col.label }}</th>
                    <th>تاريخ الحذف</th>
                    <th>حُذف بواسطة</th>
                    <th class="text-center pe-3">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in store.items" :key="item.id">
                    <td class="ps-3">
                      <input type="checkbox" :checked="store.selected.includes(item.id)" @change="store.toggleSelected(item.id)" />
                    </td>
                    <td v-if="!gridColumns.length">{{ item.title }}</td>
                    <td v-for="col in gridColumns" :key="col.path">{{ cellValue(item, col) }}</td>
                    <td><small>{{ dateTime(item.deleted_at) }}</small></td>
                    <td>{{ item.deleted_by_name || '—' }}</td>
                    <td class="text-center pe-3">
                      <div class="icon-action-group justify-content-center">
                        <button class="icon-action-btn icon-action-btn--edit" v-tooltip.top="'استرجاع'" :disabled="store.acting" @click="restoreOne(item)">
                          <i class="pi pi-replay"></i>
                        </button>
                        <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف نهائي'" :disabled="store.acting" @click="forceDeleteOne(item)">
                          <i class="pi pi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile card list -->
            <div class="mobile-list">
              <div v-for="item in store.items" :key="item.id" class="mobile-list-card">
                <div class="mobile-list-card__top">
                  <input type="checkbox" :checked="store.selected.includes(item.id)" @change="store.toggleSelected(item.id)" />
                  <div class="mobile-list-card__meta">
                    <div class="mobile-list-card__title">{{ item.title }}</div>
                    <span v-if="gridColumns[0]" class="badge-pill">{{ cellValue(item, gridColumns[0]) }}</span>
                  </div>
                </div>
                <p class="text-muted small mb-2">
                  <template v-for="col in gridColumns.slice(1)" :key="col.path">{{ col.label }}: {{ cellValue(item, col) }} · </template>
                  حُذف {{ dateTime(item.deleted_at) }} بواسطة {{ item.deleted_by_name || '—' }}
                </p>
                <div class="mobile-list-card__actions">
                  <button class="ds-btn ds-btn--secondary ds-btn--sm" :disabled="store.acting" @click="restoreOne(item)"><i class="pi pi-replay"></i> استرجاع</button>
                  <button class="ds-btn ds-btn--secondary ds-btn--sm" :disabled="store.acting" @click="forceDeleteOne(item)"><i class="pi pi-trash"></i> حذف نهائي</button>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-center mt-3" v-if="store.pagination && store.pagination.last_page > 1">
              <pagination :data="store.pagination" @pagination-change-page="store.fetchItems" class="pagination mb-0" />
            </div>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import pagination from 'laravel-vue-pagination';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useRecycleBinStore } from '@/stores/useRecycleBinStore.js';

export default {
  name: 'RecycleBinIndex',
  components: { Breadcrumb, Loading, pagination },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'سلة المحذوفات', path: '/recycle-bin' }],
    };
  },
  computed: {
    store() { return useRecycleBinStore(); },
    activeModuleInfo() {
      return this.store.modules.find((m) => m.key === this.store.activeModule) || null;
    },
    allSelected() {
      return this.store.items.length > 0 && this.store.selected.length === this.store.items.length;
    },
    gridColumns() {
      return this.activeModuleInfo?.columns || [];
    },
  },
  methods: {
    // Resolves a dot-path (e.g. "user.academic_year.name") against
    // item.data — this is what makes the grid generic across every module's
    // shape without a per-module Vue template. See config/recycle.php.
    cellValue(item, col) {
      const value = col.path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), item.data);
      return value === null || value === undefined || value === '' ? '—' : value;
    },
    dateTime(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    reload() { this.store.fetchItems(1); },
    resetFilters() {
      this.store.filters.search = '';
      this.store.filters.date_from = '';
      this.store.filters.date_to = '';
      this.store.filters.deleted_by = '';
      this.store.filters.sort = '';
      this.store.filters.direction = 'desc';
      this.store.fetchItems(1);
    },
    async selectModule(key) {
      if (key === this.store.activeModule) return;
      this.store.setModule(key);
      const info = this.activeModuleInfo;
      if (info?.implemented) {
        await Promise.all([this.store.fetchItems(1), this.store.fetchDeleters()]);
      }
    },
    async restoreOne(item) {
      try {
        await this.store.restore(item.id);
        Swal.fire({ icon: 'success', title: 'تم الاسترجاع', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
    async forceDeleteOne(item) {
      const res = await Swal.fire({
        title: 'حذف نهائي', html: `سيتم حذف <strong>${item.title}</strong> نهائيًا ولا يمكن التراجع عن ذلك.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف نهائيًا',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.forceDelete(item.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف النهائي', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
    async restoreSelected() {
      try {
        await this.store.restoreSelected();
        Swal.fire({ icon: 'success', title: 'تم الاسترجاع', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
    async forceDeleteSelected() {
      const res = await Swal.fire({
        title: 'حذف نهائي للمحدد', html: `سيتم حذف <strong>${this.store.selected.length}</strong> عنصر نهائيًا ولا يمكن التراجع عن ذلك.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف نهائيًا',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.forceDeleteSelected();
        Swal.fire({ icon: 'success', title: 'تم الحذف النهائي', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
    async restoreAll() {
      const res = await Swal.fire({
        title: 'استرجاع الكل', text: 'سيتم استرجاع جميع العناصر المحذوفة في هذا القسم.',
        icon: 'question', showCancelButton: true, confirmButtonText: 'نعم، استرجاع الكل', cancelButtonText: 'إلغاء',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.restoreAll();
        Swal.fire({ icon: 'success', title: 'تم الاسترجاع', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
    async emptyBin() {
      const res = await Swal.fire({
        title: 'إفراغ سلة المحذوفات', text: 'سيتم حذف جميع العناصر في هذا القسم نهائيًا ولا يمكن التراجع عن ذلك.',
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، إفراغ السلة',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.emptyBin();
        Swal.fire({ icon: 'success', title: 'تم إفراغ السلة', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
  },
  async mounted() {
    await this.store.fetchModules();
    if (this.activeModuleInfo?.implemented) {
      await Promise.all([this.store.fetchItems(1), this.store.fetchDeleters()]);
    }
  },
};
</script>

<style scoped>
.recycle-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}
.recycle-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--surface-border);
  background: #fff;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s ease;
}
.recycle-tab:not(.recycle-tab--disabled):hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}
.recycle-tab--active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}
.recycle-tab--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.recycle-tab__count {
  background: rgba(255,255,255,0.25);
  border-radius: 999px;
  padding: 0.05rem 0.5rem;
  font-size: 0.75rem;
}
.recycle-tab--active .recycle-tab__count { background: rgba(255,255,255,0.3); }
.recycle-tab:not(.recycle-tab--active) .recycle-tab__count { background: var(--primary-light); color: var(--primary-color); }
.recycle-tab__soon {
  font-size: 0.6875rem;
  color: var(--text-muted);
  border: 1px dashed var(--surface-border);
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
}

.bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}
.bulk-bar__select-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}
.bulk-bar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
